/**
 * Echtzeit-Lückenprüfung für Wohnsitze und Beschäftigungen.
 *
 * Die Behörde verlangt beide lückenlos für die letzten 5 Jahre; Lücken
 * über 28 Tagen führen zu Offenliste-Einträgen (ws_luecke/bv_luecke) und
 * verzögern den Antrag. Diese Prüfung läuft live bei jeder Eingabe, damit
 * das Mitglied Lücken sofort sieht — nicht erst beim Absenden (oder
 * schlimmer: erst durch die Behörde).
 *
 * Reine Logik ohne React — testbar in luecken.test.ts.
 */

export interface Zeitraum {
  von: Date | string;
  bis?: Date | string | null;
}

export interface Luecke {
  von: Date;
  bis: Date;
  tage: number;
}

export interface LueckenBefund {
  luecken: Luecke[];          // Lücken > toleranzTage im 5-Jahres-Fenster
  fehltAktuell: boolean;      // kein laufender (offener) Eintrag
  leer: boolean;              // noch gar keine Einträge
}

/**
 * Gibt es einen LAUFENDEN Eintrag (offenes Enddatum)? Die Behörde verlangt
 * je einen aktuellen Wohnsitz und ein aktuelles Beschäftigungsverhältnis
 * (Offenliste ws_aktuell/bv_aktuell). Ein Eintrag „bis heute" reicht NICHT —
 * er erzeugt keine Lücke und rutschte deshalb früher durch die Prüfung.
 */
export function hatOffenenEintrag(eintraege: Zeitraum[]): boolean {
  return (eintraege || []).some((e) => e.bis === undefined || e.bis === null || e.bis === '');
}

const TOLERANZ_TAGE = 28;     // Behörden-Toleranz (bv_luecke > 28 Tage)
const FENSTER_JAHRE = 5;
const TAG_MS = 86_400_000;

function alsDate(d: Date | string): Date {
  return d instanceof Date ? d : new Date(`${String(d).slice(0, 10)}T00:00:00`);
}

export function findeLuecken(eintraege: Zeitraum[], jetzt: Date = new Date()): LueckenBefund {
  const fensterStart = new Date(jetzt);
  fensterStart.setFullYear(fensterStart.getFullYear() - FENSTER_JAHRE);

  const gueltige = eintraege
    .filter((e) => e.von)
    .map((e) => ({
      von: alsDate(e.von),
      bis: e.bis ? alsDate(e.bis) : null,
    }))
    .filter((e) => !isNaN(e.von.getTime()) && (e.bis === null || !isNaN(e.bis.getTime())))
    // Nur was ins 5-Jahres-Fenster hineinragt, ist relevant.
    .filter((e) => e.bis === null || e.bis.getTime() >= fensterStart.getTime())
    .sort((a, b) => a.von.getTime() - b.von.getTime());

  if (gueltige.length === 0) {
    return { luecken: [], fehltAktuell: true, leer: true };
  }

  const luecken: Luecke[] = [];
  // Abdeckung beginnt am Fenster-Start; alles davor interessiert nicht.
  let abgedecktBis = fensterStart.getTime();
  let offenerEintrag = false;

  for (const e of gueltige) {
    const von = Math.max(e.von.getTime(), fensterStart.getTime());
    if (von > abgedecktBis) {
      const tage = Math.round((von - abgedecktBis) / TAG_MS);
      if (tage > TOLERANZ_TAGE) {
        luecken.push({ von: new Date(abgedecktBis), bis: new Date(von), tage });
      }
    }
    const bis = e.bis === null ? jetzt.getTime() + TAG_MS : e.bis.getTime() + TAG_MS;
    if (bis > abgedecktBis || von <= abgedecktBis) {
      abgedecktBis = Math.max(abgedecktBis, bis);
    }
    if (e.bis === null) offenerEintrag = true;
  }

  // Lücke am Ende: letzter Eintrag endete vor (jetzt - Toleranz).
  if (!offenerEintrag && jetzt.getTime() - abgedecktBis > TOLERANZ_TAGE * TAG_MS) {
    const tage = Math.round((jetzt.getTime() - abgedecktBis) / TAG_MS);
    luecken.push({ von: new Date(abgedecktBis), bis: new Date(jetzt), tage });
  }

  return { luecken, fehltAktuell: !offenerEintrag, leer: false };
}
